import React, { useEffect, useState } from 'react';

import {
  Box,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { MoneySend, Printer, ReceiptAdd, StatusUp } from 'iconsax-react';

import { useSelector, useTranslation } from 'third-party';

import { useSuperAdmin } from 'modules/superAdmin';

import { superAdminSelectors } from 'selectors';

import { palette } from 'styles/theme';

type Period = 'day' | 'month' | 'year';

const GRADIENT_CREATED_ID = 'organizationBarGradientCreated';
const GRADIENT_PRINTED_ID = 'organizationBarGradientPrinted';
const GRADIENT_MONEY_ID = 'organizationBarGradientMoney';

const PERIOD_LABEL_KEYS = {
  day: 'superAdmin.stats.period.day',
  month: 'superAdmin.stats.period.month',
  year: 'superAdmin.stats.period.year',
} as const;

// Colors per payment status for the breakdown pie; unknown statuses fall back below.
const STATUS_COLORS: Record<string, string> = {
  Draft: palette.secondary100,
  Requested: palette.secondary500,
  Created: palette.primary500,
  Void: palette.error500,
  OpenToPrint: palette.info500,
  TryToPrint: palette.info700,
  CouldNotPrint: palette.error800,
  Printed: palette.primary700,
  Emailed: palette.secondary700,
  Delivered: palette.success600,
  NotDelivered: palette.warning600,
  Cleared: palette.success800,
  Scheduled: palette.warning700,
};
const FALLBACK_COLORS = [
  palette.primary500,
  palette.secondary500,
  palette.info500,
  palette.warning600,
  palette.success600,
  palette.error500,
];

const moneyAxisFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const moneyFullFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const ChartHeading: React.FC<{
  icon: React.ReactNode;
  title: string;
}> = ({ icon, title }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2}>
    {icon}
    <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>
      {title}
    </Typography>
  </Box>
);

const NoChartData = () => {
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height={200}
      sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
    >
      <Typography color="text.secondary" fontSize="0.8rem">
        {t('superAdmin.stats.noData')}
      </Typography>
    </Box>
  );
};

type Props = {
  organizationId: string;
};

export const OrganizationStatisticsTab: React.FC<Props> = ({
  organizationId,
}) => {
  const { t } = useTranslation();
  const { loadOrgStats } = useSuperAdmin();

  const [period, setPeriod] = useState<Period>('month');

  const stats = useSelector(superAdminSelectors.orgStats);
  const statsPending = useSelector(superAdminSelectors.loadOrgStatsPending);

  useEffect(() => {
    if (organizationId) {
      loadOrgStats(organizationId, period);
    }
  }, [organizationId, period, loadOrgStats]);

  const createdData = stats?.checksCreated ?? [];
  const printedData = stats?.checksPrinted ?? [];
  const moneyData = stats?.moneyIssued ?? [];
  const statusData = stats?.statusBreakdown ?? [];

  const statusPieData = statusData.map((d, i) => ({
    id: i,
    value: d.count,
    label: d.status,
    color:
      STATUS_COLORS[d.status] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));

  return (
    <Box px={3.5} py={2.5}>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id={GRADIENT_CREATED_ID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.secondary800} />
            <stop offset="100%" stopColor={palette.primary900} />
          </linearGradient>
          <linearGradient id={GRADIENT_PRINTED_ID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.primary700} />
            <stop offset="100%" stopColor={palette.primary300} />
          </linearGradient>
          <linearGradient id={GRADIENT_MONEY_ID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.success700} />
            <stop offset="100%" stopColor={palette.success500} />
          </linearGradient>
        </defs>
      </svg>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
          {t(PERIOD_LABEL_KEYS[period])}
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_e, val) => val && setPeriod(val as Period)}
          size="small"
        >
          <ToggleButton value="day" sx={{ fontSize: '0.72rem', px: 1.5 }}>
            {t('superAdmin.stats.periodToggle.day')}
          </ToggleButton>
          <ToggleButton value="month" sx={{ fontSize: '0.72rem', px: 1.5 }}>
            {t('superAdmin.stats.periodToggle.month')}
          </ToggleButton>
          <ToggleButton value="year" sx={{ fontSize: '0.72rem', px: 1.5 }}>
            {t('superAdmin.stats.periodToggle.year')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {statsPending ? (
        <Box display="flex" justifyContent="center" pt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={4}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
            gap={4}
          >
            <Box flex={1} minWidth={0}>
              <ChartHeading
                icon={
                  <ReceiptAdd
                    variant="TwoTone"
                    size="16"
                    color={palette.primary900}
                    style={{ flexShrink: 0 }}
                  />
                }
                title={t('superAdmin.stats.checksCreated')}
              />
              {createdData.every(d => d.count === 0) ? (
                <NoChartData />
              ) : (
                <BarChart
                  height={280}
                  series={[
                    {
                      data: createdData.map(d => d.count),
                      label: t('superAdmin.stats.checksCreated'),
                      color: `url(#${GRADIENT_CREATED_ID})`,
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: createdData.map(d => d.date),
                      tickLabelStyle: { fontSize: 10 },
                    },
                  ]}
                  yAxis={[{ tickMinStep: 1 }]}
                  margin={{ top: 10, right: 16, bottom: 56, left: 44 }}
                  grid={{ horizontal: true }}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', lg: 'block' } }}
            />
            <Divider sx={{ display: { xs: 'block', lg: 'none' } }} />

            <Box flex={1} minWidth={0}>
              <ChartHeading
                icon={
                  <Printer
                    variant="TwoTone"
                    size="16"
                    color={palette.primary700}
                    style={{ flexShrink: 0 }}
                  />
                }
                title={t('superAdmin.stats.checksPrinted')}
              />
              {printedData.every(d => d.count === 0) ? (
                <NoChartData />
              ) : (
                <BarChart
                  height={280}
                  series={[
                    {
                      data: printedData.map(d => d.count),
                      label: t('superAdmin.stats.checksPrinted'),
                      color: `url(#${GRADIENT_PRINTED_ID})`,
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: printedData.map(d => d.date),
                      tickLabelStyle: { fontSize: 10 },
                    },
                  ]}
                  yAxis={[{ tickMinStep: 1 }]}
                  margin={{ top: 10, right: 16, bottom: 56, left: 44 }}
                  grid={{ horizontal: true }}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
            </Box>
          </Box>

          <Divider />

          <Box
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
            gap={4}
          >
            <Box flex={1} minWidth={0}>
              <ChartHeading
                icon={
                  <MoneySend
                    variant="TwoTone"
                    size="16"
                    color={palette.success700}
                    style={{ flexShrink: 0 }}
                  />
                }
                title={t('superAdmin.stats.moneyIssued')}
              />
              {moneyData.every(d => d.amount === 0) ? (
                <NoChartData />
              ) : (
                <BarChart
                  height={280}
                  series={[
                    {
                      data: moneyData.map(d => d.amount),
                      label: t('superAdmin.stats.moneyIssued'),
                      color: `url(#${GRADIENT_MONEY_ID})`,
                      valueFormatter: v =>
                        v == null ? '' : moneyFullFormatter.format(v),
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: moneyData.map(d => d.date),
                      tickLabelStyle: { fontSize: 10 },
                    },
                  ]}
                  yAxis={[
                    {
                      valueFormatter: (v: number) =>
                        moneyAxisFormatter.format(v),
                    },
                  ]}
                  margin={{ top: 10, right: 16, bottom: 56, left: 56 }}
                  grid={{ horizontal: true }}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', lg: 'block' } }}
            />
            <Divider sx={{ display: { xs: 'block', lg: 'none' } }} />

            <Box flex={1} minWidth={0}>
              <ChartHeading
                icon={
                  <StatusUp
                    variant="TwoTone"
                    size="16"
                    color={palette.secondary700}
                    style={{ flexShrink: 0 }}
                  />
                }
                title={t('superAdmin.stats.paymentStatus')}
              />
              {statusPieData.length === 0 ? (
                <NoChartData />
              ) : (
                <PieChart
                  height={280}
                  series={[
                    {
                      data: statusPieData,
                      innerRadius: 45,
                      paddingAngle: 2,
                      cornerRadius: 4,
                      highlightScope: {
                        fade: 'global',
                        highlight: 'item',
                      },
                      valueFormatter: item => `${item.value}`,
                    },
                  ]}
                  margin={{ top: 10, right: 16, bottom: 10, left: 16 }}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: {
                        vertical: 'middle',
                        horizontal: 'right',
                      },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      labelStyle: { fontSize: 11 },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
